import React, { Component } from 'react';
import { IndexRedirect } from 'react-router';

// Styles
import './App.css';

// Utils
import isElectron from './utils/is-electron';
import api from './utils/api';

// Containers
import AppContainer from './containers/AppContainer';
import LoginPage from './containers/LoginPage';
import LogoutPage from './containers/LogoutPage';
import ForgotPasswordPage from './containers/ForgotPasswordPage';
import ResetPasswordPage from './containers/ResetPasswordPage';
import RegisterPage from './containers/RegisterPage';
import WelcomePage from './containers/WelcomePage';

import BackpackPage from './containers/BackpackPage';
import BackpackPrevewer from './components/BackpackPreviewer.js';

import FeedbackPage from './containers/FeedbackPage';
import ReportPage from './containers/ReportPage';
import InvitePage from './containers/InvitePage';
import AccountPage from './containers/AccountPage';

import AdminPage from './containers/AdminPage';
import AdminKeyPage from './containers/AdminKeyPage';
import AdminInvitesPage from './containers/AdminInvitesPage';

// react-redux setup
import { Provider } from 'react-redux';
import { browserHistory, Router, Route } from 'react-router';
import { syncHistoryWithStore } from 'react-router-redux';

// grab redux store
import configureStore from './store/configure-store.js';

// grab localforage
import localforage from 'localforage';
import * as sessionActions from './actions/session-actions';
import * as launchActions from './actions/launch-actions';

// sync redux store with browser history
const store = configureStore();
const history = syncHistoryWithStore(browserHistory, store);

// IPC setup
if (isElectron()) {
  console.warn('Setting up IPC hooks...');
  const { ipcRenderer } = window.require('electron');

  // Listen for `request-auth` events, and when we receive one,
  // grab the current session data from localforage and send an
  // auth event with the data to ipcMain.
  ipcRenderer.on('request-auth', () => {
    localforage.getItem('session')
      .then((session = {}) => {
        let data = {};

        // If we have a session token in the local storage, fill out the auth message
        // with our session details.
        if (session && session.token) {
          const { user, token } = session;
          data = { user, session: { token } }
        }

        // Send data to ipcMain.
        ipcRenderer.send('auth', data);
      })
      .catch((err) => {
        console.error('Error fetching session:', err);
        ipcRenderer.send('auth', {});
      });
  });

  function sendRoomInfo() {
    const state = store.getState();
    let room = null;
    let roomName = null;
    let build = null;

    if (state.rooms.joinedRoom) {
      let joinedRoom = state.rooms.joinedRoom;
      room = joinedRoom.uuid;
      roomName = joinedRoom.name;
      build = joinedRoom.build;
    }

    ipcRenderer.send('room', { room, roomName, build });
  }

  // Listen for `request-room` events, and when we receive one,
  // grab the current room from the app state.
  ipcRenderer.on('request-room', () => {
    sendRoomInfo();
  });

  // Listen for a `vr-app-quit` event, and when we receive one,
  // dispatch a proper Redux action.
  ipcRenderer.on('vr-app-quit', (event, payload = {}) => {
    store.dispatch(launchActions.quitApp(payload.error, payload.data));
  });

  ipcRenderer.on('update-user', (event, payload) => {
    // Got new user object from Node process interacting with Accounts API,
    // update the local session!
    store.dispatch(sessionActions.updateCachedUser(payload.user));
  });
}

const requireAuth = (nextState, replace, callback) => {
  let state = store.getState();

  let { session } = state;
  if (!session.authenticated) {
    replace('/login');
  }

  callback();
};

const requireAdmin = (nextState, replace, callback) => {
  let state = store.getState();
  let { session } = state;
  if (!session.user.admin) {
    replace('/app/home');
  }

  callback();
}

/**
 * Revive the locally-saved auth state from localforage before entering the route.
 * Will ping the service and try to fetch the user for the session with the saved token.
 * If the call is successful, the session data is added to the Redux store.
 * If not, the session data is discarded from localforage.
 */
const reviveAuth = (nextState, replace, callback) => {
  localforage.getItem('session')
    .then((session = {}) => {
      // If the saved session has a token and user model, try to fetch their user model.
      // If the request 200s and comes back fine, resolve with the user + session details.
      // If the request fails, throw an INVALID SESSION error.
      // If the session doesn't exist (or doesn't have a token or user)
      if (session && session.token && session.user) {
        return api(`/users/${session.user.id}`, {
          headers: { 'Authorization': `JWT ${session.token}` }
        }).then((user) => ({ user, token: session.token }))
        .catch((err) => { throw Error('INVALID_SESSION') });
      } else {
        throw Error('NO_SESSION');
      }
    }).then((res) => {
      // Saved session is valid, update the locally saved session with the new user model,
      // and update the session token for posterity. Then, trigger a Redux `reviveSession`
      // action to add the session data into app state.
      localforage.setItem('session', { token: res.token, user: res.user });
      store.dispatch(sessionActions.reviveSession(res.token, res.user));
      callback();
    }).catch((err) => {
      // If there's a session saved but it's invalid, clear it and fall through.
      if (err.message === 'INVALID_SESSION') {
        return localforage.removeItem('session')
          .then(() => callback());
      }

      // Otherwise, just fall through.
      callback();
    });
}

class RootContainer extends Component {
  constructor(props) {
    super(props);
  }

  /**
   * If the user is in the Electron app, add the in-electron class to <body>.
   * @memberOf RootContainer
   */
  componentDidMount() {
    if (!isElectron()) {
      return;
    }

    document.body.className = `${document.body.className} in-electron`;
  }

  render() {
    let classNames = ['RootContainer'];
    if (isElectron()) {
      classNames.push('in-electron');
    }

    return (
      <div className={classNames.join(' ')}>
        {this.props.children}
      </div>
    );
  }
}

class App extends Component {
  render() {
    return (
      <Provider store={store}>
        <Router history={history}>
          {/* Login, register, reset password. */}
          <Route path="/" component={RootContainer} onEnter={reviveAuth}>
            <IndexRedirect to="/login" />
            <Route path="login" component={LoginPage} />
            <Route path="logout" component={LogoutPage} />
            <Route path="register/:inviteToken" component={RegisterPage} />
            <Route path="forgot-password" component={ForgotPasswordPage} />
            <Route path="reset-password/:resetToken" component={ResetPasswordPage} />

            {/* Main, authenticated app routes. */}
            <Route path="app" component={AppContainer} onEnter={requireAuth}>
              <Route path="home" component={WelcomePage} />
              <Route path="feedback" component={FeedbackPage} />
              <Route path="report" component={ReportPage} />
              <Route path="invite" component={InvitePage} />
              <Route path="account" component={AccountPage} />

              <Route path="backpack" component={BackpackPage}>
                <Route path=":id" component={BackpackPrevewer} />
              </Route>

              <Route path="admin" component={AdminPage} onEnter={requireAdmin}>
                <Route path="keys" component={AdminKeyPage} />
                <Route path="invites" component={AdminInvitesPage} />
              </Route>
            </Route>
          </Route>
        </Router>
      </Provider>
    );
  }
}

export default App;



// WEBPACK FOOTER //
// ./src/App.js